<template>
  <FormWrapper grid>
    <form @submit.prevent="submit">
      <FormInput name="name"
                 label="Query"
                 v-model="form.search"
                 class="sm:col-span-6"/>

      <div class="col-span-6">
        <label class="block text-sm font-medium text-gray-700">
          Vehicle
        </label>
      </div>

      <div class="relative col-span-1">
        <FormInput :show-label="false"
                   name="year"
                   placeholder="Year"
                   v-model="form.year"
                   class="sm:col-span-6"/>
      </div>

      <div class="relative col-span-2">
        <Autocomplete :show-label="false"
                      placeholder="Make"
                      :url="$route('ajax.vehicles.makes.autocomplete', { year: form.year })"
                      @change="form.make = $event"
                      :selected="form.make"
                      single
                      clear-on-blue
                      search-on-focus
                      />
      </div>

      <div class="relative col-span-2">
        <Autocomplete :show-label="false"
                      placeholder="Model"
                      :url="$route('ajax.vehicles.models.autocomplete', { year: form.year, make: form.make })"
                      @change="form.model = $event"
                      :selected="form.model"
                      single
                      clear-on-blue
                      search-on-focus
                      />
      </div>

      <div class="relative col-span-1">
        <Autocomplete :show-label="false"
                      placeholder="Submodel"
                      :url="$route('ajax.vehicles.submodels.autocomplete', {
                        year: form.year,
                        make: form.make,
                        model: form.model,
                      })"
                      @change="form.submodel = $event"
                      :selected="form.submodel"
                      single
                      clear-on-blue
                      search-on-focus
                      />
      </div>

      <div class="col-span-6"></div>

      <div class="relative col-span-2">
        <Autocomplete label="Status"
                      placeholder="Status"
                      :options="statusList"
                      @change="autocomplete.status = $event"
                      :selected="filters.status" />
      </div>

      <div class="relative col-span-2">
        <Autocomplete label="Parts"
                      placeholder="Search Part"
                      :url="$route('ajax.parts.autocomplete')"
                      @change="autocomplete.parts = $event"
                      :selected="filters.parts" />
      </div>

      <div class="relative col-span-2">
        <Tags label="Build ID"
              placeholder="Search Build"
              @change="tags.builds = $event"
              @remove="reload"
              :selected="tags.builds"
              :disabled="loading"/>
      </div>

      <div class="col-span-6"></div>

      <div class="col-span-1"
           v-if="expanded">
        <Autocomplete label="SEAT CONFIGURATION"
                      :options="seatConfiguration"
                      @change="autocomplete.seatConfiguration = $event"
                      :selected="filters.seat_configuration"
                      static
                      search-on-focus />
      </div>

      <div class="col-span-1"
           v-if="expanded"
           v-for="item in searchableOptions"
           :key="item.id">
        <Autocomplete :label="item.name.toUpperCase()"
                      :options="item.values"
                      @change="form.option_values[item.id] = $event"
                      :selected="filters.option_values[item.id]"
                      static
                      search-on-focus />
      </div>

      <FormActions :show-cancel="false"
                   :disabled="loading"
                   submit-label="Search"
                   grid>
        <template v-slot:more>
          <button type="button"
                  v-if="showDeleteButton && this.tags.builds.length"
                  @click="deleteMatchingBuilds"
                  class="bg-red-600 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-white hover:bg-white hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  :disabled="loading">
            Delete Matching Builds
          </button>

          <button type="button"
                  class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  @click="expanded = !expanded">
            <span v-if="expanded">Less Filters</span>
            <span v-else>
              More Filters <em v-if="moreActiveFiltersCount" class="not-italic">({{ moreActiveFiltersCount }})</em>
            </span>
          </button>
        </template>
      </FormActions>
    </form>
  </FormWrapper>
</template>

<script>
import { nextTick } from 'vue'
import FormComponent from '@/pages/form-component'
import PageComponent from '@/pages/page-component'
import { Inertia } from '@inertiajs/inertia'
import Autocomplete from '@/components/form-autocomplete'
import Tags from '@/components/form-tags.vue'

export default {
  mixins: [
    PageComponent,
    FormComponent,
  ],

  props: {
    options: Array,
    filters: Object,
    seatConfiguration: Array,
    showDeleteButton: {
      type: Boolean,
      default: false,
    },
    statusList: Array,
  },

  components: {
    Autocomplete,
    Tags,
  },

  data() {
    return {
      expanded: false,
      loading: false,
      form: {
        search: this.search,
        make: this.filters.make,
        model: this.filters.model,
        year: this.filters.year,
        submodel: this.filters.submodel,
        option_values: this.options.reduce(
          (carry, option) => {
            return {
              ...carry,
              [option.id]: (this.filters.option_values[option.id] || []).map(({ id }) => id),
            }
          },
          {},
        ),
      },
      tags: {
        builds: this.filters.builds,
      },
      autocomplete: {
        parts: this.filters.parts.map(({ id }) => id),
        seatConfiguration: this.filters.seat_configuration.map(({ id }) => id),
        status: this.filters.status.map(({ id }) => id),
      },
    }
  },

  computed: {
    searchableOptions() {
      return this.options.filter(item => ! /EXCEPTION/i.test(item.name))
        .filter(item => ! /^seat_configuration_.+/.test(item.slug))
        .map(item => ({
          ...item,
          values: item.values.map(({ id, value }) => ({
            id: id,
            name: value === ''
              ? '(empty)'
              : value,
            })),
        }))
    },

    moreActiveFiltersCount() {
      const options = Object.keys(this.form.option_values)
        .filter(id => this.form.option_values[id].length)
        .length

      const seatConfigs = this.autocomplete.seatConfiguration.length

      return options + seatConfigs
    },
  },

  methods: {
    submit() {
      this.loading = true
      Inertia.get(this.$route('builds.index'), this.buildQueryParams(), {
        onFinish: () => {
          this.loading = false
        }
      })
    },

    reload() {
      // remove is fired after change
      // to make sure everything is set correctly,
      // submit should be triggerd on the next tick of Vue cycle
      nextTick(() => {
        this.submit()
      })
    },

    deleteMatchingBuilds() {
      if (! confirm('Are you sure you want to delete Builds based on your search query? This action cannot be undone')) {
        return
      }

      const params = new URLSearchParams(this.buildQueryParams())
      const url = this.$route('builds.destroy-matching') + `?${params}`

      Inertia.delete(url, {
        preserveState: false,
        preserveScroll: false,
      })
    },

    buildQueryParams() {
      const {option_values, ...form} = this.form
      const data = {
        ...form,
        parts: this.autocomplete.parts.join(','),
        seat_configuration: this.autocomplete.seatConfiguration.join(','),
        status: this.autocomplete.status.join(','),
        builds: this.tags.builds.join(','),
      }

      const options = Object.keys(option_values)
        .reduce(
          (carry, optionId) => {
            return this.form.option_values[optionId]?.length
              ? {
                ...carry,
                [`option[${optionId}]`]: this.form.option_values[optionId].join(','),
              }
              : carry
          },
          {}
        )

      const queryParams = Object
        .entries({
          ...data,
          ...options,
        })
        .filter(([name, value]) => value);

      return Object.fromEntries(queryParams);
    }
  },
}
</script>
